import React from "react";
import { Star, Trophy } from "lucide-react";
import { formatPercent, formatRating, formatScore } from "../lib/scoringUtils";

function rankBg(rank) {
  if (rank === 1) return { background: "#ffe4e6", color: "#be123c" };
  if (rank === 2) return { background: "#fef3c7", color: "#b45309" };
  if (rank === 3) return { background: "#d9f3f2", color: "#028D93" };
  return { background: "#f1f5f9", color: "#475569" };
}

function avatarBg(rank) {
  const tones = [
    { background: "#ffe4e6", color: "#be123c" },
    { background: "#fef3c7", color: "#b45309" },
    { background: "#cffafe", color: "#0e7490" },
    { background: "#fae8ff", color: "#a21caf" },
    { background: "#ede9fe", color: "#7c3aed" },
  ];
  return tones[(rank - 1) % tones.length];
}

export default function LeaderboardTable({ rows }) {
  if (!rows.length) {
    return (
      <div
        style={{
          borderRadius: "16px",
          border: "1px solid #dfe8ee",
          background: "#fff",
          padding: "48px 24px",
          textAlign: "center",
          boxShadow: "0 10px 26px rgba(36,55,70,0.06)",
        }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "56px",
            height: "56px",
            borderRadius: "50%",
            background: "#d9f3f2",
            color: "#028D93",
            marginBottom: "16px",
          }}
        >
          <Trophy style={{ width: "28px", height: "28px" }} />
        </div>
        <h3
          style={{
            fontSize: "18px",
            fontWeight: 700,
            color: "#00293A",
            marginBottom: "8px",
          }}
        >
          Leaderboard in Progress
        </h3>
        <p
          style={{
            fontSize: "14px",
            color: "#64748b",
            maxWidth: "420px",
            margin: "0 auto",
            lineHeight: 1.5,
          }}
        >
          Rankings will be published here once the current feedback cycle concludes.
        </p>
      </div>
    );
  }

  const thStyle = {
    padding: "16px 20px",
    fontSize: "12px",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: "#64748b",
  };

  return (
    <div
      style={{
        overflow: "hidden",
        borderRadius: "16px",
        border: "1px solid #dfe8ee",
        background: "#fff",
        boxShadow: "0 16px 35px rgba(36,55,70,0.08)",
      }}
    >
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#f8fafc", textAlign: "left" }}>
              <th style={thStyle}>Rank</th>
              <th style={thStyle}>Mentor</th>
              <th style={{ ...thStyle, textAlign: "right" }}>Average Rating</th>
              <th style={{ ...thStyle, textAlign: "right" }}>Engagement Score</th>
              <th style={{ ...thStyle, textAlign: "right" }}>Final Score</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              // Support both data shapes:
              // • Public view: flat mentor_name, mentor_roll_number, rank columns
              // • Admin base table: nested mentors.name, mentors.roll_number
              const rank = row.rank ?? index + 1;
              const mentorName = row.mentor_name ?? row.mentors?.name ?? "Unknown mentor";
              const mentorRoll = row.mentor_roll_number ?? row.mentors?.roll_number ?? "-";
              const initials = mentorName
                .split(" ")
                .map((part) => part[0])
                .join("")
                .slice(0, 2)
                .toUpperCase();
              return (
                <tr
                  key={row.id}
                  style={{
                    background: "#fff",
                    borderBottom: "1px solid #dfe8ee",
                  }}
                >
                  <td style={{ padding: "16px 20px" }}>
                    <span
                      style={{
                        display: "inline-flex",
                        height: "32px",
                        minWidth: "32px",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "8px",
                        padding: "0 8px",
                        fontSize: "14px",
                        fontWeight: 700,
                        ...rankBg(rank),
                      }}
                    >
                      {rank}
                    </span>
                  </td>
                  <td style={{ padding: "16px 20px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <span
                        style={{
                          display: "flex",
                          height: "48px",
                          width: "48px",
                          flexShrink: 0,
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "50%",
                          fontSize: "14px",
                          fontWeight: 900,
                          ...avatarBg(rank),
                        }}
                      >
                        {initials}
                      </span>
                      <div>
                        <p style={{ fontWeight: 700, color: "#00293A" }}>
                          {mentorName}
                        </p>
                        <p style={{ fontSize: "12px", color: "#64748b" }}>
                          {mentorRoll}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: "16px 20px", textAlign: "right" }}>
                    <span
                      style={{
                        borderRadius: "9999px",
                        background: "#d9f3f2",
                        padding: "4px 12px",
                        fontWeight: 700,
                        color: "#028D93",
                      }}
                    >
                      {formatRating(row.review).replace(" / 5", "")}
                    </span>
                  </td>
                  <td style={{ padding: "16px 20px", textAlign: "right" }}>
                    <span
                      style={{
                        borderRadius: "9999px",
                        background: "#d9f3f2",
                        padding: "4px 12px",
                        fontWeight: 700,
                        color: "#028D93",
                      }}
                    >
                      {formatPercent(row.engagement_score).replace("%", "")}
                    </span>
                  </td>
                  <td style={{ padding: "16px 20px", textAlign: "right" }}>
                    <span
                      style={{
                        borderRadius: "9999px",
                        background: "#f1f5f9",
                        padding: "8px 16px",
                        fontSize: "16px",
                        fontWeight: 700,
                        color: "#00293A",
                      }}
                    >
                      {formatScore(row.final_score).replace(" / 100", "")}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
